/**
 * Logging Service - Production-Ready Logging
 * 
 * Replaces all console.log statements with proper logging levels
 * and production-safe output.
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  SILENT = 4
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
  context?: string;
  data?: any;
}

class LoggingService {
  private currentLevel: LogLevel;
  private isDevelopment: boolean;

  constructor() {
    // Use Vite's import.meta.env instead of process.env (browser-safe)
    this.isDevelopment = import.meta.env.DEV;
    // In production: only log WARN and ERROR (suppress DEBUG/INFO)
    // In development: log everything from DEBUG up
    this.currentLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
  }

  setLevel(level: LogLevel): void {
    this.currentLevel = level;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.currentLevel;
  }

  private formatMessage(level: LogLevel, message: string, context?: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const levelName = LogLevel[level];
    const contextStr = context ? `[${context}]` : '';
    
    if (data) {
      return `${timestamp} ${levelName} ${contextStr} ${message} ${JSON.stringify(data)}`;
    }
    return `${timestamp} ${levelName} ${contextStr} ${message}`;
  }

  private log(level: LogLevel, message: string, context?: string, data?: any): void {
    if (!this.shouldLog(level)) return;

    const formattedMessage = this.formatMessage(level, message, context, data);
    
    // Use appropriate console method based on level
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(formattedMessage);
        break;
      case LogLevel.INFO:
        console.info(formattedMessage);
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage);
        break;
      case LogLevel.ERROR:
        console.error(formattedMessage);
        break;
    }
  }

  debug(message: string, context?: string, data?: any): void {
    this.log(LogLevel.DEBUG, message, context, data);
  }

  info(message: string, context?: string, data?: any): void {
    this.log(LogLevel.INFO, message, context, data);
  }

  warn(message: string, context?: string, data?: any): void {
    this.log(LogLevel.WARN, message, context, data);
  }

  error(message: string, context?: string, data?: any): void {
    this.log(LogLevel.ERROR, message, context, data);
  }

  // Convenience methods for common patterns
  dataReceived(source: string, count: number, context?: string): void {
    this.debug(`Data received from ${source}: ${count} records`, context);
  }

  performance(operation: string, duration: number, context?: string): void {
    this.info(`${operation} completed in ${duration}ms`, context);
  }

  apiCall(endpoint: string, status: number, duration?: number, context?: string): void {
    const message = `API ${endpoint} returned ${status}${duration ? ` in ${duration}ms` : ''}`;
    if (status >= 400) {
      this.error(message, context);
    } else {
      this.info(message, context);
    }
  }

  chartRendered(chartType: string, dataPoints: number, context?: string): void {
    this.debug(`Chart ${chartType} rendered with ${dataPoints} data points`, context);
  }

  cacheHit(key: string, context?: string): void {
    this.debug(`Cache hit for key: ${key}`, context);
  }

  cacheMiss(key: string, context?: string): void {
    this.debug(`Cache miss for key: ${key}`, context);
  }
}

// Export singleton instance
export const logger = new LoggingService();

// Export convenience functions for easy migration
export const log = {
  debug: (message: string, context?: string, data?: any) => logger.debug(message, context, data),
  info: (message: string, context?: string, data?: any) => logger.info(message, context, data),
  warn: (message: string, context?: string, data?: any) => logger.warn(message, context, data),
  error: (message: string, context?: string, data?: any) => logger.error(message, context, data),
  dataReceived: (source: string, count: number, context?: string) => logger.dataReceived(source, count, context),
  performance: (operation: string, duration: number, context?: string) => logger.performance(operation, duration, context),
  apiCall: (endpoint: string, status: number, duration?: number, context?: string) => logger.apiCall(endpoint, status, duration, context),
  chartRendered: (chartType: string, dataPoints: number, context?: string) => logger.chartRendered(chartType, dataPoints, context),
  cacheHit: (key: string, context?: string) => logger.cacheHit(key, context),
  cacheMiss: (key: string, context?: string) => logger.cacheMiss(key, context)
};
